import { useEffect, useRef, useState } from 'react';
import { useMemoizedFn } from 'ahooks';
import { Button } from 'antd';
import styled from '@emotion/styled';
import { noop } from 'lodash-es';
import { SyncOutlined } from '@ant-design/icons';
import { Refresh } from './CompatQrPay';
import { useTranslate } from '@tolgee/react';

const Container = styled.div`
  position: relative;
  width: 100%;
`;

export interface PayWithSubWindowProps {
  src: string;
  name: string;
  expired: boolean;
  onRefresh: () => Promise<any>;
}

function PayWithSubWindow(props: PayWithSubWindowProps) {
  const { src, name, expired, onRefresh } = props;
  const subWindowRef = useRef<Window | null>(null);

  const { t } = useTranslate();

  const [loading, setLoading] = useState(false);
  const [isSubWindowOpened, setIsSubWindowOpened] = useState(false);

  const openSubWindow = useMemoizedFn((src) => {
    const win = window.open(
      `${window.location.href.split('#')[0]}#/pingpong`,
      name,
      'location=no,resizable,scrollbars,status',
    );
    subWindowRef.current = win;
    if (!win) {
      return win;
    }
    win.location.href = src;
    setIsSubWindowOpened(true);
    win.onbeforeunload = () => {
      setLoading(false);
      subWindowRef.current = null;
      setIsSubWindowOpened(false);
    };
    return win;
  });

  useEffect(() => {
    if (!src) {
      return noop;
    }
    setLoading(true);
    openSubWindow(src);
    const onFocus = () => {
      if (subWindowRef.current) {
        // console.log('focus');
        subWindowRef.current.focus?.();
      }
    };
    window.addEventListener('focus', onFocus);
    return () => {
      try {
        /**
         * electron 子窗口未正常关闭时可能导致子窗口指针仍然有值，但调用异常
         * Error: IPC method called after context was released
         */
        subWindowRef.current?.close?.();
      } catch (e) {
        // do nothing
      }
      subWindowRef.current = null;
      window.removeEventListener('focus', onFocus);
    };
  }, [openSubWindow, src]);

  return (
    <Container>
      {expired ? (
        <Refresh
          onClick={() => {
            if (!loading) {
              setLoading(true);

              onRefresh().finally(() => {
                setLoading(false);
              });
            }
          }}
        >
          <SyncOutlined spin={loading} />
          {t('market.重新加载')}
        </Refresh>
      ) : (
        <>
          {isSubWindowOpened ? (
            <SyncOutlined spin />
          ) : (
            <Button type="primary" onClick={() => openSubWindow(src)}>
              {t('market.继续支付')}
            </Button>
          )}
        </>
      )}
    </Container>
  );
}

export default PayWithSubWindow;
